import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export const isPushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function getRegistration() {
  await navigator.serviceWorker.register("/sw.js");
  return navigator.serviceWorker.ready;
}

// Safari < 16 uses a callback-based API; normalise to a promise.
function requestPermission() {
  return new Promise((resolve) => {
    try {
      const r = Notification.requestPermission((p) => resolve(p));
      if (r && typeof r.then === "function") r.then(resolve, () => resolve(Notification.permission));
    } catch (e) { resolve(Notification.permission); }
  });
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false, permission: "default" };
  try {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { supported: true, subscribed: !!sub, permission: Notification.permission };
  } catch (e) {
    return { supported: true, subscribed: false, permission: Notification.permission };
  }
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Notifications non supportées sur cet appareil.");
  if (Notification.permission === "denied") {
    throw new Error("Notifications bloquées : autorisez-les dans les réglages de votre navigateur ou de l'application.");
  }
  const reg = await getRegistration();
  const permission = Notification.permission === "granted" ? "granted" : await requestPermission();
  if (permission !== "granted") throw new Error("Autorisation refusée.");

  const { data } = await api.get("/push/vapid-public-key");
  if (!data || data.enabled === false || !data.publicKey) {
    throw new Error("Notifications push non configurées sur le serveur.");
  }
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function sendTestPush() {
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) throw new Error("Aucun abonnement actif sur cet appareil.");
  try {
    await api.post("/push/test", { endpoint: sub.endpoint });
    return { sent: true };
  } catch (e) {
    const status = e.response?.status;
    if (status === 404 || status === 410) {
      try { await sub.unsubscribe(); } catch (_) {}
      return { sent: false, expired: true, message: e.response?.data?.detail || "Abonnement expiré : notifications désactivées." };
    }
    throw new Error(e.response?.data?.detail || "Échec de l'envoi de la notification test.");
  }
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    try { await api.delete("/push/subscribe", { data: sub.toJSON() }); } catch (e) {}
    await sub.unsubscribe();
  }
  return true;
}
